import React, { useEffect, useState } from 'react';
import { getDocumentsType, GetEmployeeDocuments, insertemployeedocuments } from '../../services/dataService';
import toast from 'react-hot-toast';
import { IMAGE_URL } from '../../services/apiGateway';
import { IconEdit } from '@tabler/icons-react';
import { getSecureItem } from '../../services/SecureStorage';

const EmployeeDocument = ({ onClose, EmpId }) => {
  const UID = getSecureItem("userId");
  const [documents, setDocuments] = useState([]);
  const [documentFields, setDocumentFields] = useState([{
    docType: "",
    description: "",
    file: null,
    previewUrl: null,
    isEditing: false,
    isCompressing: false,
    compressionInfo: null
  }]);
  const [existingDocs, setExistingDocs] = useState([]);
  const [editingDoc, setEditingDoc] = useState(null);
  const [loading, setloading] = useState(false);

  // Enhanced Image Compression Function
  const compressImage = (file, options = {}) => {
    const {
      quality = 0.8,
      maxWidth = 1200,
      maxHeight = 1200,
      outputFormat = 'image/jpeg',
      maintainAspectRatio = true,
      autoOptimize = true
    } = options;

    return new Promise((resolve, reject) => {
      if (!file || !file.type.startsWith('image/')) {
        reject(new Error('Invalid file: Please provide a valid image file'));
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onerror = () => reject(new Error('Failed to load image'));

      img.onload = () => {
        try {
          let { width, height } = img;
          const originalSize = file.size;

          // Calculate new dimensions while maintaining aspect ratio
          if (maintainAspectRatio) {
            const aspectRatio = width / height;

            if (width > height) {
              if (width > maxWidth) {
                width = maxWidth;
                height = width / aspectRatio;
              }
            } else {
              if (height > maxHeight) {
                height = maxHeight;
                width = height * aspectRatio;
              }
            }

            if (width > maxWidth) {
              width = maxWidth;
              height = width / aspectRatio;
            }
            if (height > maxHeight) {
              height = maxHeight;
              width = height * aspectRatio;
            }
          } else {
            width = Math.min(width, maxWidth);
            height = Math.min(height, maxHeight);
          }

          canvas.width = Math.round(width);
          canvas.height = Math.round(height);

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

          // Auto-optimize quality based on original file size
          let finalQuality = quality;
          if (autoOptimize) {
            if (originalSize > 5 * 1024 * 1024) {
              finalQuality = Math.min(quality, 0.6);
            } else if (originalSize > 2 * 1024 * 1024) {
              finalQuality = Math.min(quality, 0.7);
            } else if (originalSize > 1 * 1024 * 1024) {
              finalQuality = Math.min(quality, 0.8);
            }
          }

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to compress image'));
              return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
              const compressedBase64 = reader.result;
              const compressionRatio = ((originalSize - blob.size) / originalSize * 100).toFixed(1);

              resolve({
                base64: compressedBase64,
                blob: blob,
                originalSize: originalSize,
                compressedSize: blob.size,
                compressionRatio: compressionRatio,
                dimensions: {
                  width: canvas.width,
                  height: canvas.height
                }
              });
            };
            reader.onerror = () => reject(new Error('Failed to convert to base64'));
            reader.readAsDataURL(blob);
          }, outputFormat, finalQuality);

        } catch (error) {
          reject(new Error(`Compression failed: ${error.message}`));
        }
      };

      img.src = URL.createObjectURL(file);
    });
  };

  // Utility function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetchData = async () => {
    try {
      const [docTypesResponse, employeeDocsResponse] = await Promise.all([
        getDocumentsType(UID),
        GetEmployeeDocuments(UID)
      ]);

      if (docTypesResponse?.data?.data) {
        setDocuments(docTypesResponse.data.data);
      }

      if (employeeDocsResponse?.data?.data) {
        const empDocs = employeeDocsResponse.data.data.filter(doc => doc.EmpId === EmpId);
        setExistingDocs(empDocs.map(doc => ({
          ...doc,
          previewUrl: `${IMAGE_URL}/${doc.Doc}`,
          isEditing: false
        })));
      }
    } catch (error) {
      toast.error("Error fetching data");
    }
  };

  useEffect(() => {
    fetchData();
  }, [UID, EmpId]);

  const handleAddDocument = () => {
    setDocumentFields([...documentFields, {
      docType: "",
      description: "",
      file: null,
      previewUrl: null,
      isCompressing: false,
      compressionInfo: null
    }]);
  };

  const handleRemoveDocument = (index) => {
    const newFields = documentFields.filter((_, idx) => idx !== index);
    setDocumentFields(newFields);
  };

  const handleFileChange = async (index, event) => {
    const fileInput = event.target;
    const file = fileInput.files[0];

    if (file) {
      const validFormats = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      const fileType = file.type;

      // Check if the file format is valid
      if (!validFormats.includes(fileType)) {
        toast.error("Accepted formats are: .jpg, .jpeg, .png, .pdf");
        fileInput.value = "";
        return;
      }

      // Check if the file size exceeds 50MB
      if (file.size > 50 * 1024 * 1024) {
        toast.error("File size must be less than 50MB");
        fileInput.value = "";
        return;
      }

      // Handle PDF files (no compression needed)
      if (file.type === "application/pdf") {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newFields = [...documentFields];
          newFields[index] = {
            ...newFields[index],
            file: file,
            previewUrl: reader.result,
            isCompressing: false,
            compressionInfo: null
          };
          setDocumentFields(newFields);
        };
        reader.readAsDataURL(file);
        return;
      }

      // Handle image files with compression
      if (file.type.startsWith('image/')) {
        try {
          // Show loading state
          const newFields = [...documentFields];
          newFields[index] = {
            ...newFields[index],
            file: file,
            previewUrl: null,
            isCompressing: true,
            compressionInfo: null
          };
          setDocumentFields(newFields);

          // Compress the image
          const compressionResult = await compressImage(file, {
            quality: 0.8,
            maxWidth: 1200,
            maxHeight: 1200,
            outputFormat: 'image/jpeg',
            autoOptimize: true
          });

          // Show compression info
          const compressionInfo = `Original: ${formatFileSize(compressionResult.originalSize)} → Compressed: ${formatFileSize(compressionResult.compressedSize)} (${compressionResult.compressionRatio}% reduction)`;

          // Update state with compressed image
          const updatedFields = [...documentFields];
          updatedFields[index] = {
            ...updatedFields[index],
            file: file,
            previewUrl: compressionResult.base64,
            isCompressing: false,
            compressionInfo: compressionInfo
          };
          setDocumentFields(updatedFields);

          // Show success message
          toast.success(`Image compressed successfully! ${compressionResult.compressionRatio}% size reduction`);

        } catch (error) {
          console.error("Compression error:", error);
          toast.error("Error compressing image. Please try again.");

          // Reset the field on error
          const newFields = [...documentFields];
          newFields[index] = {
            ...newFields[index],
            file: null,
            previewUrl: null,
            isCompressing: false,
            compressionInfo: null
          };
          setDocumentFields(newFields);
          fileInput.value = "";
        }
      }
    }
  };

  const handleFieldChange = (index, field, value) => {
    const newFields = [...documentFields];
    newFields[index] = {
      ...newFields[index],
      [field]: value
    };
    setDocumentFields(newFields);
  };

  const handleEdit = (doc) => {
    setEditingDoc(doc);
    setDocumentFields([{
      docType: doc.DocId,
      description: doc.DocDescription || "",
      file: null,
      previewUrl: doc.previewUrl,
      Id: doc.Id,
      isCompressing: false,
      compressionInfo: null
    }]);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setloading(true);
    try {
      const field = documentFields[0];
      const data = {
        Id: field.Id,
        EmpId,
        UserId: UID,
        Doc: field.file ? field.previewUrl : editingDoc.Doc,
        DocId: field.docType,
        DocDescription: field.description
      };

      const response = await insertemployeedocuments(data);

      if (response?.data?.StatusCode === 200) {
        const updatedDocs = existingDocs.map(doc =>
          doc.Id === field.Id ? { ...doc, ...data } : doc
        );
        setExistingDocs(updatedDocs);

        setEditingDoc(null);
        setDocumentFields([{
          docType: "",
          description: "",
          file: null,
          previewUrl: null,
          isCompressing: false,
          compressionInfo: null
        }]);

        toast.success("Document updated successfully");
        setloading(false);
        fetchData();
      } else {
        toast.error(response?.data?.message || "Error updating document");
        setloading(false);
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Error updating document");
      setloading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setloading(true);
    try {
      for (const field of documentFields) {
        if (!field.docType) {
          toast.error("Please select document type for all entries");
          setloading(false);
          return;
        }
        if (!field.file && !field.Id) {
          toast.error("Please upload a file for all entries");
          setloading(false);
          return;
        }

        const data = {
          EmpId,
          UserId: UID,
          Doc: field.previewUrl,
          DocId: field.docType,
          DocDescription: field.description
        };

        const response = await insertemployeedocuments(data);
        if (response?.data?.StatusCode === 200) {
          toast.success("Documents uploaded successfully");
        }
      }
      setloading(false);
      onClose();
    } catch (error) {
      toast.error("Error uploading documents");
      setloading(false);
    }
  };

  const clearForm = () => {
    setDocumentFields([{
      docType: "",
      description: "",
      file: null,
      previewUrl: null,
      isCompressing: false,
      compressionInfo: null
    }]);
  };

  const cancelEdit = () => {
    setEditingDoc(null);
    setDocumentFields([{
      docType: "",
      description: "",
      file: null,
      previewUrl: null,
      isCompressing: false,
      compressionInfo: null
    }]);
  };

  return (
    <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6 max-h-[60vh] overflow-auto cus-scrollbar">
      <h2 className="text-2xl font-semibold mb-6 text-center">Employee Documents</h2>

      {/* Uploaded Documents Table */}
      {existingDocs.length > 0 && !editingDoc && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Uploaded Documents</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full border rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Document</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {existingDocs.map((doc, index) => (
                  <tr key={doc.Id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p
                        onClick={() => window.open(doc.previewUrl, '_blank')}
                        className="text-blue hover:text-blue cursor-pointer"
                      >
                        View Document
                      </p>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-gray-700 font-medium">
                        {documents.find(t => t.Id === doc.DocId)?.Name || 'Document Name'}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-gray-600 truncate max-w-xs">{doc.DocDescription || 'No description'}</p>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        className="bg-sky rounded-[4px] px-10 py-[8px]"
                        onClick={() => handleEdit(doc)}
                      >
                        <IconEdit className="text-white w-[18px] h-[18px]" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Section */}
      <div>
        <h3 className="text-lg font-medium">Add Documents</h3>

        <form onSubmit={editingDoc ? handleUpdate : handleSubmit} className="space-y-6">
          {documentFields.map((field, index) => (
            <div key={index} className="border rounded-lg p-4 relative">
              {!editingDoc && documentFields.length > 1 && (
                <button
                  type="button"
                  className="absolute top-2 right-2 text-red-500"
                  onClick={() => handleRemoveDocument(index)}
                >
                  Remove
                </button>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-1 font-medium">
                    Document Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={field.docType}
                    onChange={(e) => handleFieldChange(index, 'docType', e.target.value)}
                    className="w-full p-2 border rounded"
                    required
                  >
                    <option value="">Select Type</option>
                    {documents.map((doc) => (
                      <option key={doc.Id} value={doc.Id}>{doc.Name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block mb-1 font-medium">Description</label>
                  <textarea
                    value={field.description}
                    onChange={(e) => handleFieldChange(index, 'description', e.target.value)}
                    className="w-full p-2 border rounded"
                    placeholder="Enter description"
                  />
                </div>
              </div>

              {/* Current Document Preview for Edit Mode */}
              {editingDoc && index === 0 && (
                <div className="mt-5">
                  <div className="flex justify-between items-center mb-3">
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        window.open(editingDoc.previewUrl, '_blank');
                      }}
                      className="btn btn-primary"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                      View Current Document
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="block mb-1 font-medium">
                  {editingDoc ? 'Replace Document (Optional)' : 'Document'} {!editingDoc && <span className="text-red-500">*</span>}
                  <span className="ml-1 text-sm text-gray-500">(Accepted formats: JPG, JPEG, PNG, PDF. Maximum size: 50 MB)</span>
                </label>
                <input
                  type="file"
                  onChange={(e) => handleFileChange(index, e)}
                  className="w-full p-2 border rounded"
                  accept=".jpg,.jpeg,.png,.pdf"
                  required={!editingDoc}
                />

                {/* Loading state during compression */}
                {field.isCompressing && (
                  <div className="mt-3 p-3 border rounded bg-blue-50 flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    <span className="text-blue-600">Compressing image...</span>
                  </div>
                )}

                {/* Show compression info */}
                {field.compressionInfo && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                    <strong>Compression Results:</strong> {field.compressionInfo}
                  </div>
                )}

                {/* Preview section */}
                {!editingDoc && field.previewUrl && !field.isCompressing && (
                  <div className="mt-3 p-3 border rounded bg-gray-50">
                    <p className="mb-2 font-medium">Selected Document Preview:</p>
                    {field.previewUrl.includes("data:image/") ? (
                      <img
                        src={field.previewUrl}
                        alt="Preview"
                        className="mt-2 max-w-xs max-h-40 object-contain rounded"
                      />
                    ) : (
                      <div className="mt-2 flex items-center">
                        <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                        </svg>
                        <span className="ml-2 font-medium">PDF Document</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}

          <div className="flex justify-between items-center pt-4 mt-6 border-t">
            <button
              type="button"
              onClick={clearForm}
              className="btn btn-secondary"
            >
              Clear
            </button>

            <div className="flex gap-4">
              {editingDoc ? (
                <>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="btn btn-danger"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Updating..." : "Update"}
                  </button>
                </>
              ) : (
                <>
                  <div>
                    {!editingDoc && documentFields.length < 5 && (
                      <button
                        type="button"
                        onClick={handleAddDocument}
                        className="btn btn-success flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                        </svg>
                        Add Another Document
                      </button>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? "Uploading..." : "Submit"}
                  </button>
                </>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeDocument;